/*
 * /sonar-depth-map — pictures of the grid.
 *
 *   SDMRender.palette(name) → (t ∈ [0,1]) → [r, g, b]
 *   SDMRender.overlay(res, o) → canvas, one pixel per grid node: depth colour
 *       times hillshade, empty where the map has no data
 *   SDMRender.slopeOverlay(res) → canvas (steepness, for «where is the edge»)
 *   SDMRender.compose(res, o) → Promise<canvas>: the shareable picture —
 *       basemap, depth, isobaths with labels, holes and humps, legend, scale,
 *       north arrow, title and the accuracy line
 *
 * The hillshade exaggerates relief (lake bottoms are flat at map scale: a
 * 2 m slope over 50 m would be invisible at true scale); the light comes from
 * the north-west, the classic cartographic sun.
 */
(function (root) {
    'use strict';

    var PALETTES = {
        // chart blue: light shallows, dark deep water
        blue: [[0, [222, 245, 255]], [0.2, [178, 225, 247]], [0.45, [112, 184, 232]], [0.7, [52, 124, 204]], [1, [16, 52, 128]]],
        // echo-sounder colours: red shallows → violet deep
        sonar: [[0, [214, 52, 46]], [0.16, [240, 136, 44]], [0.33, [238, 208, 66]], [0.5, [92, 190, 92]], [0.66, [52, 186, 196]], [0.83, [48, 104, 214]], [1, [94, 48, 168]]]
    };
    function palette(name) {
        var stops = PALETTES[name] || PALETTES.blue;
        return function (t) {
            if (!(t >= 0)) t = 0; if (t > 1) t = 1;
            for (var i = 1; i < stops.length; i++) {
                if (t <= stops[i][0]) {
                    var a = stops[i - 1], b = stops[i], f = (t - a[0]) / (b[0] - a[0]);
                    return [a[1][0] + (b[1][0] - a[1][0]) * f, a[1][1] + (b[1][1] - a[1][1]) * f, a[1][2] + (b[1][2] - a[1][2]) * f];
                }
            }
            return stops[stops.length - 1][1].slice();
        };
    }

    function range(res) {
        var lo = Math.max(0, res.stats.dmin), hi = res.stats.dmax;
        if (!(hi > lo)) hi = lo + 1;
        return [lo, hi];
    }

    function makeCanvas(w, h) {
        if (typeof OffscreenCanvas !== 'undefined' && typeof document === 'undefined') return new OffscreenCanvas(w, h);
        var c = document.createElement('canvas'); c.width = w; c.height = h; return c;
    }

    function overlay(res, o) {
        o = o || {};
        var g = res.geo, w = g.w, h = g.h, D = res.depth;
        var pal = palette(o.palette), rg = range(res), lo = rg[0], span = rg[1] - rg[0];
        var c = makeCanvas(w, h), ctx = c.getContext('2d'), img = ctx.createImageData(w, h), px = img.data;
        var z = o.exaggeration || 12, cell = g.cell;
        // light from the north-west, 45° up
        var lx = -Math.SQRT1_2 * 0.7071, ly = Math.SQRT1_2 * 0.7071, lz = 0.7071;
        for (var r = 0; r < h; r++) {
            for (var col = 0; col < w; col++) {
                var i = r * w + col, v = D[i], k = i * 4;
                if (v !== v) { px[k + 3] = 0; continue; }
                var rgb = pal((v - lo) / span);
                var shade = 1;
                if (o.hillshade !== false) {
                    var l = col > 0 ? D[i - 1] : v, rt = col < w - 1 ? D[i + 1] : v, u = r > 0 ? D[i - w] : v, dn = r < h - 1 ? D[i + w] : v;
                    if (l !== l) l = v; if (rt !== rt) rt = v; if (u !== u) u = v; if (dn !== dn) dn = v;
                    // surface height = −depth; gradient east (x) and north (y)
                    var dzx = -(rt - l) / (2 * cell) * z, dzy = -(u - dn) / (2 * cell) * z;
                    var nx = -dzx, ny = -dzy, nz = 1, nl = Math.sqrt(nx * nx + ny * ny + nz * nz);
                    var lam = (nx * lx + ny * ly + nz * lz) / nl;
                    shade = 0.62 + 0.5 * Math.max(0, lam);
                    if (shade > 1.12) shade = 1.12;
                }
                px[k] = Math.min(255, rgb[0] * shade);
                px[k + 1] = Math.min(255, rgb[1] * shade);
                px[k + 2] = Math.min(255, rgb[2] * shade);
                px[k + 3] = o.alpha == null ? 235 : o.alpha;
            }
        }
        ctx.putImageData(img, 0, 0);
        return c;
    }

    function slopeOverlay(res) {
        var g = res.geo, w = g.w, h = g.h, S = res.slope;
        var c = makeCanvas(w, h), ctx = c.getContext('2d'), img = ctx.createImageData(w, h), px = img.data;
        for (var i = 0; i < w * h; i++) {
            var s = S[i], k = i * 4;
            if (s !== s || s < 3) { px[k + 3] = 0; continue; }
            var t = Math.min(1, (s - 3) / 17);
            px[k] = 255; px[k + 1] = Math.round(200 - 170 * t); px[k + 2] = 40;
            px[k + 3] = Math.round(60 + 170 * t);
        }
        ctx.putImageData(img, 0, 0);
        return c;
    }

    // ── shareable picture ──────────────────────────────────────────────────
    var TILE = 256, RAD = Math.PI / 180;
    function mpx(lat, lon, z) {
        var n = Math.pow(2, z) * TILE, s = Math.sin(Math.max(-85.05, Math.min(85.05, lat)) * RAD);
        return [(lon + 180) / 360 * n, (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n];
    }

    function bounds(res) {
        var g = res.geo;
        return { north: g.lat0 + g.dLat / 2, south: g.lat0 - (g.h - 0.5) * g.dLat, west: g.lon0 - g.dLon / 2, east: g.lon0 + (g.w - 0.5) * g.dLon };
    }

    // Tight bounds of the cells that have data (the grid carries padding).
    function dataBounds(res) {
        var g = res.geo, D = res.depth, r0 = g.h, r1 = -1, c0 = g.w, c1 = -1;
        for (var r = 0; r < g.h; r++) for (var c = 0; c < g.w; c++) { if (D[r * g.w + c] === D[r * g.w + c]) { if (r < r0) r0 = r; if (r > r1) r1 = r; if (c < c0) c0 = c; if (c > c1) c1 = c; } }
        if (r1 < 0) return bounds(res);
        return { north: g.lat0 - r0 * g.dLat + g.dLat / 2, south: g.lat0 - r1 * g.dLat - g.dLat / 2, west: g.lon0 + c0 * g.dLon - g.dLon / 2, east: g.lon0 + c1 * g.dLon + g.dLon / 2 };
    }

    function niceScale(metres) {
        var p = Math.pow(10, Math.floor(Math.log10(metres))), n = metres / p;
        return (n >= 5 ? 5 : n >= 2 ? 2 : 1) * p;
    }

    // o: { width, height, palette, units ('m'|'ft'), fmtDepth(v) → text,
    //      title, subtitle, footer, legendTitle, basemap: bool, layers {...},
    //      featureLabel(kind, f), brand, credit(mapSource) }
    function compose(res, o) {
        o = o || {};
        var Wd = o.width || 2000, Ht = o.height || 1400;
        var head = 92, foot = 74;
        var mapH = Ht - head - foot, mapW = Wd;
        var b = dataBounds(res);
        var z = 22, A, B;
        for (; z > 2; z--) {
            A = mpx(b.north, b.west, z); B = mpx(b.south, b.east, z);
            if (B[0] - A[0] <= mapW - 160 && B[1] - A[1] <= mapH - 120) break;
        }
        A = mpx(b.north, b.west, z); B = mpx(b.south, b.east, z);
        var cx = (A[0] + B[0]) / 2, cy = (A[1] + B[1]) / 2;
        var left = cx - mapW / 2, top = cy - mapH / 2;
        var c = makeCanvas(Wd, Ht), ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, Wd, Ht);
        var X = 0, Y = head;
        function P(lat, lon) { var p = mpx(lat, lon, z); return [X + p[0] - left, Y + p[1] - top]; }
        var mapSource = null;
        var bm = (o.basemap !== false && root.__mapTiles && root.__mapTiles.snapshot)
            ? root.__mapTiles.snapshot({ style: 'light', zoom: z, x: left, y: top, width: mapW, height: mapH, pixelRatio: 1 }).then(function (cv) {
                if (cv) { ctx.drawImage(cv, X, Y, mapW, mapH); mapSource = cv.mapSource || 'vector'; }
            }, function () {})
            : Promise.resolve();
        return bm.then(function () {
            ctx.save();
            ctx.beginPath(); ctx.rect(X, Y, mapW, mapH); ctx.clip();
            if (!mapSource) { ctx.fillStyle = '#eef2f6'; ctx.fillRect(X, Y, mapW, mapH); }
            var L = o.layers || {};
            if (L.depth !== false) {
                var ov = overlay(res, { palette: o.palette, alpha: 255 });
                var gb = bounds(res), p0 = P(gb.north, gb.west), p1 = P(gb.south, gb.east);
                ctx.imageSmoothingEnabled = true;
                ctx.globalAlpha = 0.92;
                ctx.drawImage(ov, p0[0], p0[1], p1[0] - p0[0], p1[1] - p0[1]);
                ctx.globalAlpha = 1;
            }
            if (L.contours !== false) {
                (res.contours || []).forEach(function (cl) {
                    var major = Math.abs(cl.level / (res.interval * 5) - Math.round(cl.level / (res.interval * 5))) < 1e-6;
                    ctx.strokeStyle = major ? 'rgba(10,30,70,.95)' : 'rgba(15,40,90,.72)';
                    ctx.lineWidth = major ? 2.4 : 1.3;
                    cl.lines.forEach(function (a) {
                        ctx.beginPath();
                        for (var i = 0; i < a.length; i += 2) { var q = P(a[i + 1], a[i]); if (i === 0) ctx.moveTo(q[0], q[1]); else ctx.lineTo(q[0], q[1]); }
                        ctx.stroke();
                    });
                });
                // labels: the middle of every line long enough to carry one
                ctx.font = '600 15px system-ui,-apple-system,Segoe UI,sans-serif';
                ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
                var placed = [];
                (res.contours || []).forEach(function (cl) {
                    cl.lines.forEach(function (a) {
                        var n = a.length / 2;
                        if (n < 6) return;
                        var len = 0, pts = [];
                        for (var i = 0; i < n; i++) pts.push(P(a[2 * i + 1], a[2 * i]));
                        for (var i2 = 1; i2 < n; i2++) len += Math.hypot(pts[i2][0] - pts[i2 - 1][0], pts[i2][1] - pts[i2 - 1][1]);
                        if (len < 120) return;
                        var m = pts[Math.floor(n / 2)];
                        for (var k = 0; k < placed.length; k++) if (Math.hypot(placed[k][0] - m[0], placed[k][1] - m[1]) < 90) return;
                        placed.push(m);
                        var txt = o.fmtDepth ? o.fmtDepth(cl.level) : String(cl.level);
                        var tw = ctx.measureText(txt).width + 8;
                        ctx.fillStyle = 'rgba(255,255,255,.88)';
                        ctx.fillRect(m[0] - tw / 2, m[1] - 10, tw, 20);
                        ctx.fillStyle = '#0b2350';
                        ctx.fillText(txt, m[0], m[1] + 1);
                    });
                });
            }
            if (L.features !== false && res.features) {
                var mark = function (list, kind, color, up) {
                    list.forEach(function (f) {
                        var q = P(f.lat, f.lon);
                        ctx.beginPath();
                        if (up) { ctx.moveTo(q[0], q[1] - 11); ctx.lineTo(q[0] + 10, q[1] + 7); ctx.lineTo(q[0] - 10, q[1] + 7); }
                        else { ctx.moveTo(q[0], q[1] + 11); ctx.lineTo(q[0] + 10, q[1] - 7); ctx.lineTo(q[0] - 10, q[1] - 7); }
                        ctx.closePath();
                        ctx.fillStyle = color; ctx.fill();
                        ctx.lineWidth = 2; ctx.strokeStyle = '#fff'; ctx.stroke();
                        var txt = o.featureLabel ? o.featureLabel(kind, f) : '';
                        if (txt) {
                            ctx.font = '700 14px system-ui,-apple-system,Segoe UI,sans-serif';
                            ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
                            ctx.lineWidth = 4; ctx.lineJoin = 'round'; ctx.strokeStyle = 'rgba(255,255,255,.95)';
                            ctx.strokeText(txt, q[0] + 14, q[1]);
                            ctx.fillStyle = '#16233d'; ctx.fillText(txt, q[0] + 14, q[1]);
                        }
                    });
                };
                mark(res.features.holes.slice(0, 12), 'hole', '#d6304a', false);
                mark(res.features.humps.slice(0, 12), 'hump', '#e08a18', true);
            }
            ctx.restore();
            ctx.textAlign = 'left';

            // header
            ctx.fillStyle = '#0d1b33';
            ctx.font = '800 34px system-ui,-apple-system,Segoe UI,sans-serif'; ctx.textBaseline = 'alphabetic';
            ctx.fillText(o.title || '', 28, 48);
            ctx.fillStyle = '#4a5a74'; ctx.font = '500 18px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.fillText(o.subtitle || '', 28, 76);

            // legend (gradient bar, top right of the map)
            var rg = range(res), pal = palette(o.palette);
            var lw = 300, lh = 16, lx = Wd - lw - 36, ly = head + 24;
            ctx.fillStyle = 'rgba(255,255,255,.92)'; ctx.fillRect(lx - 14, ly - 30, lw + 28, lh + 62);
            for (var i = 0; i < lw; i++) { var col = pal(i / (lw - 1)); ctx.fillStyle = 'rgb(' + Math.round(col[0]) + ',' + Math.round(col[1]) + ',' + Math.round(col[2]) + ')'; ctx.fillRect(lx + i, ly, 1, lh); }
            ctx.fillStyle = '#16233d'; ctx.font = '700 14px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.fillText(o.legendTitle || '', lx, ly - 10);
            ctx.font = '500 13px system-ui,-apple-system,Segoe UI,sans-serif';
            var legTxt = o.fmtLegend || o.fmtDepth;
            ctx.textAlign = 'left'; ctx.fillText(legTxt ? legTxt(rg[0]) : rg[0].toFixed(1), lx, ly + lh + 18);
            ctx.textAlign = 'right'; ctx.fillText(legTxt ? legTxt(rg[1]) : rg[1].toFixed(1), lx + lw, ly + lh + 18);
            ctx.textAlign = 'left';

            // scale bar (bottom left of the map) — metres per pixel at this zoom and latitude
            var latc = (b.north + b.south) / 2;
            var mpp = 156543.03392 * Math.cos(latc * RAD) / Math.pow(2, z);
            var useFt = o.units === 'ft';
            var unitM = useFt ? 0.3048 : 1;
            var target = niceScale(160 * mpp / unitM);
            var barPx = target * unitM / mpp;
            var sx = 28, sy = head + mapH - 30;
            ctx.fillStyle = 'rgba(255,255,255,.9)'; ctx.fillRect(sx - 8, sy - 26, barPx + 16 + 60, 40);
            ctx.fillStyle = '#16233d'; ctx.fillRect(sx, sy, barPx, 5);
            ctx.fillRect(sx, sy - 6, 2, 11); ctx.fillRect(sx + barPx - 2, sy - 6, 2, 11);
            ctx.font = '600 14px system-ui,-apple-system,Segoe UI,sans-serif';
            // fmtLength takes metres and prints in the page's unit
            ctx.fillText((o.fmtLength ? o.fmtLength(target * unitM) : target + (useFt ? ' ft' : ' m')), sx, sy - 10);

            // north arrow
            var nx = Wd - 48, ny = head + mapH - 70;
            ctx.beginPath(); ctx.moveTo(nx, ny - 22); ctx.lineTo(nx + 11, ny + 10); ctx.lineTo(nx, ny + 3); ctx.lineTo(nx - 11, ny + 10); ctx.closePath();
            ctx.fillStyle = '#16233d'; ctx.fill();
            ctx.font = '800 16px system-ui,-apple-system,Segoe UI,sans-serif'; ctx.textAlign = 'center'; ctx.fillText('N', nx, ny - 28); ctx.textAlign = 'left';

            // footer
            ctx.fillStyle = '#f3f5f8'; ctx.fillRect(0, Ht - foot, Wd, foot);
            ctx.fillStyle = '#2a3850'; ctx.font = '500 16px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.fillText(o.footer || '', 28, Ht - foot + 30);
            ctx.fillStyle = '#6a7890'; ctx.font = '500 13px system-ui,-apple-system,Segoe UI,sans-serif';
            var credit = mapSource && root.__mapTiles && root.__mapTiles.creditText ? root.__mapTiles.creditText(mapSource) : '';
            ctx.fillText((o.brand || '') + (credit ? '  ·  ' + credit : ''), 28, Ht - foot + 56);
            return c;
        });
    }

    var API = { palette: palette, overlay: overlay, slopeOverlay: slopeOverlay, compose: compose, bounds: bounds, dataBounds: dataBounds, range: range, PALETTES: PALETTES };
    root.SDMRender = API;
    if (typeof module !== 'undefined' && module.exports) module.exports = API;
})(typeof self !== 'undefined' ? self : globalThis);
